<template>
    <div>
        <dynamic-toolbar
            title="Reports"
            :loading="loading.generic"
            color="pink"
            icon="mdi-chart-box-multiple"
            theme="dark"
        >
        </dynamic-toolbar>

        <v-card elevation="2">
            <v-card-title>
                <v-row class="mt-1">
                    <v-col cols="12" md="3">
                        <v-select
                            v-model="selectedReport"
                            :items="reportList"
                            item-text="name"
                            item-value="id"
                            label="Report"
                            persistent-hint
                            return-object
                            hint="Select the report type"
                            dense
                            outlined
                            prepend-inner-icon="mdi-chart-box-outline"
                            :menu-props="{ top: false, offsetY: true }"
                        ></v-select>
                    </v-col>
                    <v-col
                        cols="12"
                        md="3"
                    >
                        <v-select
                            v-if="checkReportType('enrollment-report')"
                            v-model="params.filterBy"
                            :items="filterByList.generic"
                            label="Filter by"
                            item-text="text"
                            item-value="value"
                            persistent-hint
                            hint="Select the filter type"
                            dense
                            outlined
                            prepend-inner-icon="mdi-filter-menu"
                            :menu-props="{ top: false, offsetY: true }"
                        ></v-select>
                        <div
                            v-else
                            style="width: 294px;"
                        ></div>
                    </v-col>
                    <v-col
                        cols="12"
                        md="4"
                    >
                        <v-autocomplete
                            v-if="checkReportType('enrollment-report') && params.filterBy == 'by-driver'"
                            v-model="params.filterValue"
                            :items="collections.drivers"
                            label="Driver"
                            item-text="name"
                            item-value="id"
                            persistent-hint
                            return-object
                            hint="Select the driver"
                            dense
                            outlined
                            prepend-inner-icon="mdi-account-filter"
                            :menu-props="{ top: false, offsetY: true }"
                            :disabled="params.filterBy != 'by-driver'"
                        ></v-autocomplete>
                        <div
                            v-else
                            style="width: 400px;"
                        ></div>
                    </v-col>
                </v-row>
                
                <v-row class="mt-1">
                    <v-col cols="12" class="d-flex justify-end pt-0">
                        <v-btn
                            color="success"
                            depressed
                            @click="getReport('json')"
                        >
                            <v-icon left>
                                mdi-chart-tree
                            </v-icon>
                            Get Report
                        </v-btn>
                        <v-btn
                            color="primary"
                            depressed
                            @click="getReport('blob')"
                            class="ml-4"
                        >
                            <v-icon left>
                                mdi-download-box
                            </v-icon>
                            Download
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            
            <v-divider></v-divider>

            <v-card-text v-if="contexts.missing_documents_ctx.show">
                <v-row class="mt-1">
                    <v-col>
                        <v-simple-table dense>
                            <thead>
                                <tr>
                                    <th
                                        v-for="(value, key) in contexts.missing_documents_ctx.headers"
                                        :key="key"
                                    >
                                        {{ value }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(itm, key) in contexts.missing_documents_ctx.data"
                                    :key="key"
                                >
                                    <td
                                        v-for="(value, key) in contexts.missing_documents_ctx.headers"
                                        :key="key"
                                        v-html="itm[value]"
                                    >
                                        {{ itm[value] }}
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-card-text v-if="contexts.enrollment_report_ctx.show">
                <v-row style="margin-bottom: 20px;">
                    <v-col
                        class="text-h4 mt-3"
                        cols="5"
                        style="color: #006064;"
                    >
                        <v-icon color="cyan darken-4">mdi-text-box-search</v-icon>
                        {{ selectedReport.name }}
                    </v-col>
                    <v-col cols="5">
                        <v-text-field
                            v-model="contexts.enrollment_report_ctx.search"
                            label="Search"
                            hide-details
                            prepend-inner-icon="mdi-magnify"
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="2">
                        <v-switch
                            v-model="contexts.enrollment_report_ctx.grouped_by_course"
                            @change="handleGroupedValue"
                            inset
                            label="Group by course"
                            color="cyan darken-4"
                            :disabled="params.filterBy != 'all-drivers'"
                        ></v-switch>
                    </v-col>
                </v-row>
                <v-row class="mt-1">
                    <v-col cols="12">
                        <v-data-iterator
                            :items="contexts.enrollment_report_ctx.data[contexts.enrollment_report_ctx.grouped_value]"
                            :search="contexts.enrollment_report_ctx.search"
                            :custom-filter="customFilter"
                        >
                            <template v-slot:default="{ items }">
                                <v-row>
                                    <v-col v-for="(chunk, chunkIndex) in items" :key="chunkIndex" cols="12">
                                        <v-card v-if="chunk.length" outlined style="border: unset;">
                                            <v-card-text style="margin: 0;padding: 0;">
                                                <v-expansion-panels multiple v-model="chunk[0].default_expanded" flat focusable>
                                                    <v-expansion-panel>
                                                        <v-expansion-panel-header>
                                                            <v-row>
                                                                <v-col cols="12" md="12" class="chunk-title">
                                                                    <v-chip
                                                                        color="cyan darken-2"
                                                                        dark
                                                                        label
                                                                        style="font-size: medium;"
                                                                    >
                                                                        <v-icon left>
                                                                            {{ 
                                                                                contexts.enrollment_report_ctx.grouped_by_course
                                                                                ? 'mdi-school'
                                                                                : 'mdi-account'
                                                                            }}
                                                                        </v-icon>
                                                                        {{
                                                                            contexts.enrollment_report_ctx.grouped_by_course
                                                                                ? chunk[0].course_name
                                                                                : chunk[0].driver_name
                                                                        }}
                                                                    </v-chip>
                                                                </v-col>
                                                            </v-row>
                                                        </v-expansion-panel-header>
                                                        <v-expansion-panel-content>
                                                            <v-row>
                                                                <v-col cols="12" md="12" style="padding-left: 0;padding-top: 0;">
                                                                    <v-card style="border: unset;" outlined>
                                                                        <v-card-title class="equal-height">
                                                                            <v-row>
                                                                                <v-col cols="12" md="12" class="headline text-left">
                                                                                    <v-icon left>
                                                                                        {{
                                                                                            contexts.enrollment_report_ctx.grouped_by_course
                                                                                                ? 'mdi-account'
                                                                                                : 'mdi-school'
                                                                                        }}
                                                                                    </v-icon>
                                                                                    {{ 
                                                                                        contexts.enrollment_report_ctx.grouped_by_course
                                                                                            ? 'Drivers'
                                                                                            : 'Courses'
                                                                                    }}
                                                                                </v-col>
                                                                            </v-row>
                                                                        </v-card-title>

                                                                        <v-divider></v-divider>

                                                                        <v-card-text>
                                                                            <v-row>
                                                                                <v-col cols="12" md="12" style="padding-top: 10px;">
                                                                                    <v-data-table
                                                                                        :headers="contexts.enrollment_report_ctx.headers[contexts.enrollment_report_ctx.grouped_value]"
                                                                                        :items="chunk"
                                                                                        style="padding: 0;"
                                                                                        dense
                                                                                    ></v-data-table>
                                                                                </v-col>
                                                                            </v-row>
                                                                        </v-card-text>
                                                                    </v-card>
                                                                </v-col>
                                                            </v-row>
                                                        </v-expansion-panel-content>
                                                    </v-expansion-panel>
                                                </v-expansion-panels>
                                            </v-card-text>
                                        </v-card>
                                        <v-divider></v-divider>
                                    </v-col>
                                </v-row>
                            </template>
                        </v-data-iterator>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </div>
</template>
<script>
import axios from 'axios';
import DynamicToolbar from '../../components/Global/Utils/DynamicToolbar.vue';

export default {
    components: {
        DynamicToolbar
    },
    data: function() {
        return {
            loading: {
                generic: false
            },
            contexts: {
                missing_documents_ctx: {
                    show: false,
                    data: [],
                    headers: [],
                },
                enrollment_report_ctx: {
                    show: false,
                    data: {
                        grouped_by_driver: [],
                        grouped_by_course: []
                    },
                    headers: {
                        grouped_by_driver: [
                            { text: 'Course', value: 'course_name' },
                            { text: 'Status', value: 'enrollment_status' },
                            { text: 'Score', value: 'enrollment_score' },
                            { text: 'AssignedAt', value: 'enrollment_assigned_at' },
                            { text: 'StartedAt', value: 'enrollment_started_at' },
                            { text: 'FinishedAt', value: 'enrollment_finished_at' },
                            { text: 'ExpiresAt', value: 'enrollment_expires_at' },
                        ],
                        grouped_by_course: [
                            { text: 'Driver', value: 'driver_name' },
                            { text: 'Status', value: 'enrollment_status' },
                            { text: 'Score', value: 'enrollment_score' },
                            { text: 'AssignedAt', value: 'enrollment_assigned_at' },
                            { text: 'StartedAt', value: 'enrollment_started_at' },
                            { text: 'FinishedAt', value: 'enrollment_finished_at' },
                            { text: 'ExpiresAt', value: 'enrollment_expires_at' },
                        ],
                    },
                    search: '',
                    grouped_value: 'grouped_by_driver',
                    grouped_by_course: false,
                },
            },
            selectedReport: { 
                id: 1,
                name: 'Missing Documents',
                alias: 'missing-documents',
                axios: 'driver-list/reports/missing-documents',
                context_key: 'missing_documents_ctx'
            },
            reportList: [
                { 
                    id: 1,
                    name: 'Missing Documents',
                    alias: 'missing-documents',
                    axios: 'driver-list/reports/missing-documents',
                    context_key: 'missing_documents_ctx'
                },
                {
                    id: 2,
                    name: 'Enrollment Report',
                    alias: 'enrollment-report',
                    axios: 'driver-list/reports/enrollment-report',
                    context_key: 'enrollment_report_ctx'
                },
            ],
            params: {
                responseType: 'json',
                filterBy: 'all-drivers',
                filterValue: null,
            },
            filterByList: {
                generic: [
                    { text: 'All drivers', value: 'all-drivers' },
                    { text: 'By driver', value: 'by-driver' },
                ]
            },
            collections: {},
        };
    },
    mounted() {
        this.getCollections();
    },
    methods: {
        customFilter(items, search) {
            if (!search) return items;

            search = search.toString().toLowerCase();

            return items
                .map((chunk) =>
                    chunk.filter((enrollment) => {
                        return (
                            enrollment.driver_name.toLowerCase().includes(search) ||
                            enrollment.course_name.toLowerCase().includes(search)
                        );
                    })
                )
                .filter((chunk) => chunk.length > 0);
        },
        getCollections() {
            this.loading.generic = true;
            axios
                .get('driver-list/collections')
                .then(response => {
                    this.collections = response.data;
                    this.loading.generic = false;
                })
                .catch(error => {
                    console.log(error);
                });
        },
        getReport(responseType = 'json') {
            this.$store.state.isLoading = true;
            this.loading.generic = true;
            this.params.responseType = responseType;
            let url = this.selectedReport.axios;
            let data = this.params;
            
            const config = {
                method: 'post',
                url,
                responseType,
                data
            };

            axios(config)
                .then(response => {
                    this.handleResponse(response.data, responseType);
                    this.loading.generic = false;
                    this.$store.state.isLoading = false;
                })
                .catch(error => {
                    console.log(error);
                    this.loading.generic = false;
                    this.$store.state.isLoading = false;
                });
        },
        handleResponse(data, responseType) {
            if (responseType == 'json') {
                console.log('data ', data);

                switch (this.selectedReport.alias) {
                    case 'missing-documents':
                        this.contexts.missing_documents_ctx.data = data.map(x => {
                            return {
                                Document: x.Document,
                                TotalDriv: x.TotalDriv,
                                East:
                                    '<ul style=" columns: 2;-webkit-columns: 2;-moz-columns: 2;">' +
                                    x.East +
                                    '</ul>',
                                GTA:
                                    '<ul style=" columns: 2;-webkit-columns: 2;-moz-columns: 2;">' +
                                    x.GTA +
                                    '</ul>',
                                West:
                                    '<ul style=" columns: 2;-webkit-columns: 2;-moz-columns: 2;">' +
                                    x.West +
                                    '</ul>'
                            };
                        });
                        this.contexts.missing_documents_ctx.headers = Object.keys(this.contexts.missing_documents_ctx.data[0]);
                        this.contexts.missing_documents_ctx.show = true;
                        break;
                    case 'enrollment-report':
                        this.contexts.enrollment_report_ctx.data = data;

                        // Set default expanded
                        this.contexts.enrollment_report_ctx.data.grouped_by_driver.forEach(chunk => {
                            chunk.forEach(enrollment => {
                                enrollment.default_expanded = [0];
                            });
                        });

                        this.contexts.enrollment_report_ctx.data.grouped_by_course.forEach(chunk => {
                            chunk.forEach(enrollment => {
                                enrollment.default_expanded = [0];
                            });
                        });

                        if (this.params.filterBy == 'by-driver') {
                            this.contexts.enrollment_report_ctx.grouped_by_course = false;
                        }

                        console.log(this.contexts.enrollment_report_ctx.data);
                        this.contexts.enrollment_report_ctx.show = true;
                        break
                    default:
                        break;
                }
            } else if (responseType == 'blob') {
                let prefix = 'Human Resources Reports - ';
                let filename = prefix + this.selectedReport.name;
                this.generateDownloadLink(data, filename);
            }

            this.handleContexts();
        },
        generateDownloadLink(data, filename = 'undefined-filename', extension = 'pdf') {
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename + '.' + extension);
            document.body.appendChild(link);
            link.click();
        },
        checkReportType(alias) {
            return (alias == this.selectedReport.alias);
        },
        handleContexts() {
            for (let key in this.contexts) {
                if (key != this.selectedReport.context_key) {
                    this.contexts[key].show = false;
                }
            }
        },
        handleGroupedValue() {
            this.contexts.enrollment_report_ctx.grouped_value = this.contexts.enrollment_report_ctx.grouped_by_course ? 'grouped_by_course' : 'grouped_by_driver';
        },
    },
};
</script>

<style>
    .chunk-title {
        font-size: 18px;
        font-weight: bold;
        color: #00796b;
    }
</style>